import { useState, useRef, useCallback } from 'react';

/**
 * Custom hook that provides a debounced message state management system
 * 
 * This hook allows components to display messages with a debouncing mechanism
 * that prevents rapid message changes from causing flickering or poor UX.
 * 
 * @returns {Object} An object containing:
 *   - message: The current message object with type and text properties
 *   - setMessageDebounced: Function to set a new message with debouncing
 *   - clearMessage: Function to clear the current message
 */
export const useMessageDebounced = () => {
  const [message, setMessage] = useState(null);
  const messageTimeoutRef = useRef(null);
  const messageQueueRef = useRef(null);

  /**
   * Sets a message with debouncing to prevent rapid changes
   * 
   * @param {Object|null} newMessage - The message object to display or null to clear
   * @param {string} newMessage.type - Message type ('success', 'error', 'warning', 'info')
   * @param {string} newMessage.text - Message content text
   */
  const setMessageDebounced = useCallback((newMessage) => {
    // Store the latest message
    messageQueueRef.current = newMessage;

    // If we already have a timeout scheduled, don't create another one
    if (!messageTimeoutRef.current) {
      // Set immediately if first message
      setMessage(newMessage);

      // Create a timeout to check for more messages
      messageTimeoutRef.current = setTimeout(() => {
        // If we have a newer message in queue than what's displayed, update it
        if (messageQueueRef.current !== newMessage) {
          setMessage(messageQueueRef.current);
        }
        // Clear the timeout reference
        messageTimeoutRef.current = null;
      }, 1000);
    }
  }, []);

  /**
   * Clears the current message
   */
  const clearMessage = useCallback(() => {
    setMessage(null);
    // Also clear any pending message updates
    messageQueueRef.current = null;
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = null;
    }
  }, []);

  return { message, setMessageDebounced, clearMessage };
};
